"use client";

import Link from "next/link";
import { useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { LiquidGlassCard } from "@/components/ui/liquid-glass-card";
import type { GlassVariant } from "@/components/ui/liquid-glass-card";
import { cn } from "@/lib/utils";

interface ActionWithClick {
  label: string;
  /**
   * Awaited before the toast closes. Return `false` to keep it open — a retry
   * that failed still needs its button, and a toast that vanishes on click
   * reports success it never confirmed. Any other return value closes it.
   */
  onClick: () => unknown;
  /** Shown while an async `onClick` is in flight. */
  pendingLabel?: string;
}
interface ActionWithHref {
  label: string;
  href: string;
}
type ToastAction = ActionWithClick | ActionWithHref;

interface ActionToastInput {
  id: string;
  variant?: GlassVariant;
  title: string;
  body?: string;
  primaryAction: ToastAction;
  secondaryAction?: ActionWithClick;
  /** ms before auto-close; omit to stay until dismissed. */
  duration?: number;
  /** Fires when the user hits the X button (not when toast.dismiss is called externally). */
  onDismiss?: () => void;
}

interface PersistentToastInput {
  id: string;
  variant?: GlassVariant;
  title: string;
  body?: string;
  href?: string;
  /** Fires when the user hits the X button (not when toast.dismiss is called externally). */
  onDismiss?: () => void;
}

const variantIcon: Record<GlassVariant, typeof AlertTriangle> = {
  amber: AlertTriangle,
  rose: AlertTriangle,
  cyan: Info,
  emerald: CheckCircle2,
};

const variantIconColor: Record<GlassVariant, string> = {
  amber: "text-warning",
  rose: "text-destructive",
  cyan: "text-info",
  emerald: "text-primary",
};

/**
 * Severity lives in the icon, never in the call to action. The semantic fills
 * are all 400-level in dark mode — light enough that white label text fails
 * contrast outright (`--warning` is oklch(0.86 …), a near-yellow) — and each
 * one has a matching `*-foreground` token precisely because it can't take
 * white. The CTA sidesteps the whole question by staying brand-colored, which
 * also survives per-product theming: a sky-blue "warning" would read wrong on
 * callops, a sky-blue button reads fine.
 */
const variantIconSurface: Record<GlassVariant, string> = {
  amber: "border-warning/30 bg-warning/10",
  rose: "border-destructive/30 bg-destructive/10",
  cyan: "border-info/30 bg-info/10",
  emerald: "border-primary/30 bg-primary/10",
};

const primaryButtonClass =
  "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-lg shadow-primary/25 transition-colors duration-200 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70";

function PrimaryButton({
  action,
  toastId,
}: {
  action: ToastAction;
  toastId: string;
}) {
  const [pending, setPending] = useState(false);

  if ("href" in action) {
    return (
      <Link
        href={action.href}
        className={primaryButtonClass}
        onClick={() => toast.dismiss(toastId)}
      >
        {action.label}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={primaryButtonClass}
      disabled={pending}
      onClick={() => {
        if (pending) return;
        setPending(true);
        void (async () => {
          try {
            const outcome = await action.onClick();
            if (outcome !== false) {
              toast.dismiss(toastId);
            }
          } finally {
            setPending(false);
          }
        })();
      }}
    >
      {pending ? (
        <>
          <Loader2 className="size-3 animate-spin" aria-hidden />
          {action.pendingLabel ?? action.label}
        </>
      ) : (
        action.label
      )}
    </button>
  );
}

function GlassToastBody({
  variant,
  title,
  body,
  toastId,
  primaryAction,
  secondaryAction,
  onDismiss,
}: {
  variant: GlassVariant;
  title: string;
  body?: string;
  toastId: string;
  primaryAction?: ToastAction;
  secondaryAction?: ActionWithClick;
  onDismiss?: () => void;
}) {
  const Icon = variantIcon[variant];
  return (
    <LiquidGlassCard variant={variant} glowIntensity="subtle">
      <div className="w-[360px] max-w-[calc(100vw-2rem)] p-3.5">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border",
              variantIconSurface[variant],
            )}
          >
            <Icon className={cn("h-4 w-4", variantIconColor[variant])} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h4 className="text-foreground text-sm font-semibold leading-tight">
              {title}
            </h4>
            {body ? (
              <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                {body}
              </p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label="Dismiss"
            onClick={() => {
              onDismiss?.();
              toast.dismiss(toastId);
            }}
            className="text-muted-foreground hover:bg-foreground/10 hover:text-foreground size-auto flex-shrink-0 rounded-md p-1 duration-200"
          >
            <X className="size-3.5" />
          </Button>
        </div>
        {primaryAction || secondaryAction ? (
          <div className="mt-3 flex justify-end gap-2">
            {secondaryAction ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => {
                  // Fire-and-forget by design: the secondary action is always
                  // the way out, so it never holds the toast open.
                  void secondaryAction.onClick();
                  toast.dismiss(toastId);
                }}
                className="text-muted-foreground hover:bg-foreground/10 hover:text-foreground h-auto rounded-lg px-3 py-1.5 duration-200"
              >
                {secondaryAction.label}
              </Button>
            ) : null}
            {primaryAction ? (
              <PrimaryButton action={primaryAction} toastId={toastId} />
            ) : null}
          </div>
        ) : null}
      </div>
    </LiquidGlassCard>
  );
}

export const glassToast = {
  action({
    id,
    variant = "amber",
    title,
    body,
    primaryAction,
    secondaryAction,
    duration,
    onDismiss,
  }: ActionToastInput) {
    return toast.custom(
      (t) => (
        <GlassToastBody
          variant={variant}
          title={title}
          body={body}
          toastId={String(t)}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          onDismiss={onDismiss}
        />
      ),
      { id, duration: duration ?? Infinity },
    );
  },

  persistent({
    id,
    variant = "cyan",
    title,
    body,
    href,
    onDismiss,
  }: PersistentToastInput) {
    return toast.custom(
      (t) => (
        <GlassToastBody
          variant={variant}
          title={title}
          body={body}
          toastId={String(t)}
          primaryAction={href ? { label: "Open", href } : undefined}
          onDismiss={onDismiss}
        />
      ),
      { id, duration: Infinity },
    );
  },

  dismiss(id: string) {
    toast.dismiss(id);
  },
};
